import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GlobalReferentials } from "@/components/admin/GlobalReferentials";
import { DeletionRequests } from "@/components/admin/DeletionRequests";
import { Building2, Loader2, Plus, UserMinus, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMessage } from "@/lib/api";
import { PageTitle, Loading, ErrorBox, Empty } from "@/components/common";
import { EmailSection } from "@/components/admin/EmailSection";
import { EmailTemplates } from "@/components/admin/EmailTemplates";

const AdminForm = ({ testId, onSubmit, label = "Ajouter" }) => {
  const [f, setF] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(f); setF({ name: "", email: "" }); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
      <Input data-testid={`${testId}-name`} placeholder="Nom et prénom" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className="h-11" />
      <Input data-testid={`${testId}-email`} type="email" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required className="h-11" />
      <Button data-testid={`${testId}-submit`} type="submit" disabled={busy} className="h-11">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />} {label}</Button>
    </form>
  );
};

const Workplace = ({ w, mine, meId, onChange }) => {
  const [name, setName] = useState(w.name);
  const run = async (fn, msg) => {
    try { const r = await fn(); onChange(r.data); toast.success(msg(r.data)); } catch (e) { toast.error(errMessage(e)); throw e; }
  };
  const save = (active, newName = w.name) => run(() => api.put(`/platform/workplaces/${w.id}`, { name: newName, active }), () => (active === w.active ? "Workplace enregistré." : active ? "Workplace réactivé." : "Workplace désactivé."));
  return (
    <div data-testid={`workplace-${w.id}`} className={`rounded-xl border bg-card p-5 ${w.active ? "border-border" : "border-red-500/50 opacity-80"}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Building2 className="h-5 w-5 text-amber-500" />
        <Input data-testid={`workplace-name-${w.id}`} value={name} onChange={(e) => setName(e.target.value)} className="h-10 max-w-xs font-semibold" aria-label="Nom du workplace" />
        <Button size="sm" variant="outline" data-testid={`workplace-save-${w.id}`} onClick={() => save(w.active, name.trim())} disabled={name.trim() === w.name || !name.trim()}>Renommer</Button>
        <span data-testid={`workplace-status-${w.id}`} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${w.active ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-400"}`}>{w.active ? "Actif" : "Désactivé"}</span>
        {!mine && <Button size="sm" variant="ghost" data-testid={`workplace-toggle-${w.id}`} onClick={() => save(!w.active).catch(() => {})}>{w.active ? "Désactiver" : "Réactiver"}</Button>}
        <Button asChild size="sm" variant="outline"><Link data-testid={`workplace-manage-${w.id}`} to={mine ? "/admin" : `/admin?workplace=${w.id}&nom=${encodeURIComponent(w.name)}`}>Administrer</Link></Button>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        Code d'inscription <b className="font-data text-foreground" data-testid={`workplace-code-${w.id}`}>{w.join_code}</b> · {w.counts.users} utilisateur(s) actif(s) · {w.counts.sites} site(s) · {w.counts.equipment} équipement(s)
      </p>
      <p className="mt-4 text-xs font-bold uppercase tracking-wide text-muted-foreground">Administrateurs du workplace</p>
      <ul className="mt-2 space-y-1">
        {w.admins.map((a) => (
          <li key={a.id} data-testid={`workplace-admin-${a.id}`} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm">
            <span>{a.name} <span className="text-muted-foreground">· {a.email}</span>{!a.active && <span className="ml-1 text-red-400">(désactivé)</span>}</span>
            {a.id !== meId && <Button size="sm" variant="ghost" aria-label="Retirer le rôle administrateur" data-testid={`workplace-admin-remove-${a.id}`}
              onClick={() => window.confirm(`Retirer le rôle administrateur à ${a.name} ? Il deviendra superviseur.`) && run(() => api.delete(`/platform/workplaces/${w.id}/admins/${a.id}`), () => "Rôle administrateur retiré.").catch(() => {})}>
              <UserMinus className="h-4 w-4" />
            </Button>}
          </li>
        ))}
        {!w.admins.length && <li className="text-sm text-red-400">Aucun administrateur.</li>}
      </ul>
      <div className="mt-3"><AdminForm testId={`workplace-add-admin-${w.id}`} label="Nommer administrateur"
        onSubmit={(f) => run(() => api.post(`/platform/workplaces/${w.id}/admins`, f), (d) => (d.invited ? "Administrateur créé : un e-mail d'invitation lui a été envoyé." : "Utilisateur promu administrateur.")).catch(() => {})} /></div>
    </div>
  );
};

const CreateWorkplace = ({ onCreated }) => {
  const [name, setName] = useState("");
  const [admin, setAdmin] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/platform/workplaces", { name, admin: admin.email ? admin : null });
      onCreated(r.data); setName(""); setAdmin({ name: "", email: "" });
      toast.success(admin.email ? "Workplace créé et invitation envoyée." : "Workplace créé.");
    } catch (err) { toast.error(errMessage(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="workplace-create-form" className="space-y-3 rounded-xl border border-dashed border-amber-500/50 bg-card p-5">
      <p className="font-semibold">Créer un workplace</p>
      <div><Label htmlFor="wp-name">Nom du workplace</Label><Input id="wp-name" data-testid="workplace-create-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 h-11" /></div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input data-testid="workplace-create-admin-name" placeholder="Administrateur : nom (facultatif)" value={admin.name} onChange={(e) => setAdmin({ ...admin, name: e.target.value })} required={!!admin.email} className="h-11" />
        <Input data-testid="workplace-create-admin-email" type="email" placeholder="Administrateur : e-mail (facultatif)" value={admin.email} onChange={(e) => setAdmin({ ...admin, email: e.target.value })} className="h-11" />
      </div>
      <Button data-testid="workplace-create-submit" type="submit" disabled={busy}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Créer le workplace</Button>
    </form>
  );
};

export default function Platform() {
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const [me, setMe] = useState(null);
  const load = () => Promise.all([api.get("/platform/workplaces"), api.get("/auth/me")])
    .then(([w, m]) => { setList(w.data); setMe(m.data); }).catch((e) => setError(errMessage(e)));
  useEffect(() => { load(); }, []);
  if (error) return <ErrorBox message={error} onRetry={load} />;
  if (!list) return <Loading />;
  const replace = (w) => setList((l) => (l.some((x) => x.id === w.id) ? l.map((x) => (x.id === w.id ? w : x)) : [...l, w]));
  return (
    <div className="space-y-5">
      <PageTitle kicker="Administration de la plateforme" title="Workplaces" testId="platform-title" />
      <p className="text-sm text-muted-foreground">Vous gérez les workplaces, leurs administrateurs et les référentiels globaux Take 5. Les Take 5, inspections et photos restent privés à chaque workplace.</p>
      <Tabs defaultValue="workplaces">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="workplaces" data-testid="platform-tab-workplaces" className="h-10 px-4">Workplaces ({list.length})</TabsTrigger>
          <TabsTrigger value="referentials" data-testid="platform-tab-referentials" className="h-10 px-4">Référentiels globaux</TabsTrigger>
          <TabsTrigger value="deletions" data-testid="platform-tab-deletions" className="h-10 px-4">Suppressions de compte</TabsTrigger>
          <TabsTrigger value="email" data-testid="platform-tab-email" className="h-10 px-4">E-mails</TabsTrigger>
        </TabsList>
        <TabsContent value="workplaces" className="space-y-4">
          <CreateWorkplace onCreated={replace} />
          {!list.length && <Empty testId="workplaces-empty">Aucun workplace.</Empty>}
          {list.map((w) => <Workplace key={w.id} w={w} mine={w.id === me?.org_id} meId={me?.id} onChange={replace} />)}
        </TabsContent>
        <TabsContent value="referentials"><GlobalReferentials /></TabsContent>
        <TabsContent value="deletions"><DeletionRequests /></TabsContent>
        <TabsContent value="email">
          <Tabs defaultValue="config">
            <TabsList className="mb-4">
              <TabsTrigger value="config" data-testid="email-tab-config" className="h-9 px-4">Configuration</TabsTrigger>
              <TabsTrigger value="templates" data-testid="email-tab-templates" className="h-9 px-4">Modèles</TabsTrigger>
            </TabsList>
            <TabsContent value="config"><EmailSection /></TabsContent>
            <TabsContent value="templates"><EmailTemplates /></TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
}
